import {
  acceptedNoomClinicalOffer,
  getSelectedMedPlanType,
} from "src/utils/userSegment/features";
import { PaymentEnrollmentFormState } from "src/utils/redux/slices/paymentEnrollmentForm";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "src/utils/noomSessionStorage";
import {
  SessionStorageKey,
  MedProductType,
  BRANDED_MED_ESTIMATED_VALUE,
  COMPOUNDED_MED_TELEX293_ESTIMATED_VALUE,
  COMPOUNDED_MED_24_WEEKS_ESTIMATED_VALUE,
  COMPOUNDED_MED_48_WEEKS_ESTIMATED_VALUE,
  ORALS_METFORMIN_MED_ESTIMATED_VALUE,
  HRT_PATCH_MED_ESTIMATED_VALUE,
  HRT_CREAM_MED_ESTIMATED_VALUE,
  LIRAGLUTIDE_MED_ESTIMATED_VALUE,
  COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER_MED_ESTIMATED_VALUE,
  COMPOUNDED_TIRZEPATIDE_TELEX283_MED_ESTIMATED_VALUE,
  COMPOUNDED_TIRZEPATIDE_MED_24_WEEKS_ESTIMATED_VALUE,
  COMPOUNDED_TIRZEPATIDE_MED_48_WEEKS_ESTIMATED_VALUE,
  COMPOUNDED_MICRODOSE_TIRZEPATIDE_MED_ESTIMATED_VALUE,
  COMPOUNDED_MICRODOSE_TIRZEPATIDE_MED_24_WEEKS_ESTIMATED_VALUE,
  COMPOUNDED_MICRODOSE_TIRZEPATIDE_MED_48_WEEKS_ESTIMATED_VALUE,
  MDS_MED_ESTIMATED_VALUE,
  MDS_MED_24_WEEKS_ESTIMATED_VALUE,
  MDS_MED_48_WEEKS_ESTIMATED_VALUE,
} from "src/utils/constants";
import {
  trackPurchaseIntent,
  PurchaseIntentDetails,
} from "src/utils/services/ConversionTracker";
import { appConfig } from "src/config";
import getStore from "src/utils/redux/store";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { MED_SKU } from "@utils/medEligibility";
import { trackEvent } from "src/utils/api/tracker";
import { BillingCyclePreviewProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/billing_cycle_preview";
import {
  getLongDurationWeeksFromBillingInterval,
  LongDurationWeekCount,
} from "src/checkout/multiVariant/longDurationPlans";

function getEstimatedValueForDuration(
  durationWeeks: LongDurationWeekCount | undefined,
  values: { weeks24: number; weeks48: number },
  fallback: number
): number {
  if (durationWeeks === 24) {
    return values.weeks24;
  }
  if (durationWeeks === 48) {
    return values.weeks48;
  }
  return fallback;
}

function getPurchaseIntentDetails(
  recurringBillingCycle?: BillingCyclePreviewProto
): PurchaseIntentDetails {
  const medPlanType = getSelectedMedPlanType();
  const durationWeeks = getLongDurationWeeksFromBillingInterval(
    recurringBillingCycle?.billingInterval
  );

  if (medPlanType === MED_SKU.COMPOUNDED) {
    return {
      estimatedValue: getEstimatedValueForDuration(
        durationWeeks,
        {
          weeks24: COMPOUNDED_MED_24_WEEKS_ESTIMATED_VALUE,
          weeks48: COMPOUNDED_MED_48_WEEKS_ESTIMATED_VALUE,
        },
        COMPOUNDED_MED_TELEX293_ESTIMATED_VALUE
      ),
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.HRT_CREAM) {
    return {
      estimatedValue: HRT_CREAM_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.HRT,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.HRT_PATCH) {
    return {
      estimatedValue: HRT_PATCH_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.HRT,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.ORALS_METFORMIN) {
    return {
      estimatedValue: ORALS_METFORMIN_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.Orals,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.GENERIC_LIRAGLUTIDE) {
    return {
      estimatedValue: LIRAGLUTIDE_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.COMPOUNDED_MICRODOSE) {
    return {
      estimatedValue: getEstimatedValueForDuration(
        durationWeeks,
        {
          weeks24: MDS_MED_24_WEEKS_ESTIMATED_VALUE,
          weeks48: MDS_MED_48_WEEKS_ESTIMATED_VALUE,
        },
        MDS_MED_ESTIMATED_VALUE
      ),
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER) {
    return {
      estimatedValue:
        COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER_MED_ESTIMATED_VALUE,
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.COMPOUNDED_TIRZEPATIDE) {
    return {
      estimatedValue: getEstimatedValueForDuration(
        durationWeeks,
        {
          weeks24: COMPOUNDED_TIRZEPATIDE_MED_24_WEEKS_ESTIMATED_VALUE,
          weeks48: COMPOUNDED_TIRZEPATIDE_MED_48_WEEKS_ESTIMATED_VALUE,
        },
        COMPOUNDED_TIRZEPATIDE_TELEX283_MED_ESTIMATED_VALUE
      ),
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  if (medPlanType === MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE) {
    return {
      estimatedValue: getEstimatedValueForDuration(
        durationWeeks,
        {
          weeks24:
            COMPOUNDED_MICRODOSE_TIRZEPATIDE_MED_24_WEEKS_ESTIMATED_VALUE,
          weeks48:
            COMPOUNDED_MICRODOSE_TIRZEPATIDE_MED_48_WEEKS_ESTIMATED_VALUE,
        },
        COMPOUNDED_MICRODOSE_TIRZEPATIDE_MED_ESTIMATED_VALUE
      ),
      medProductType: MedProductType.GLP1,
      medPlanType,
    };
  }
  return {
    estimatedValue: BRANDED_MED_ESTIMATED_VALUE,
    medProductType: MedProductType.GLP1,
    medPlanType,
  };
}

export function handleTermAgreementClick(
  checked: boolean,
  enrollmentInfo: PaymentEnrollmentFormState["enrollmentInfo"]
) {
  if (
    checked &&
    acceptedNoomClinicalOffer() &&
    !getNoomSessionStorage(SessionStorageKey.trackedCheckoutTermsAndConditions)
  ) {
    setNoomSessionStorage(
      SessionStorageKey.trackedCheckoutTermsAndConditions,
      true
    );

    if (appConfig.MED_PIXEL_TRACKING_ENABLED) {
      const coreState = getStore().getState();
      const surveyAnswers = getSurveyAnswers(coreState);
      const { recommendedPlan, checkoutOrders } = coreState;
      const { initialBillingCycle, recurringBillingCycle, prefetchedEltv } =
        checkoutOrders;
      const purchaseIntentDetails = getPurchaseIntentDetails(
        recurringBillingCycle
      );

      trackPurchaseIntent(
        enrollmentInfo.email,
        recommendedPlan,
        surveyAnswers,
        purchaseIntentDetails,
        initialBillingCycle,
        recurringBillingCycle,
        prefetchedEltv?.eltvValue ?? null,
        prefetchedEltv?.eltvBlendedValue ?? null
      );

      trackEvent("EltvShadowComparison", {
        hardcodedEltv: purchaseIntentDetails.estimatedValue,
        mlEltv: prefetchedEltv?.eltvValue ?? null,
        mlEltvAvailable: prefetchedEltv != null,
        medPlanType: purchaseIntentDetails.medPlanType,
        planId: recommendedPlan?.braintree_id,
      });
    }
  }
}
